import { resolveDenkerProjectAgent } from "@/lib/denker-composer-agent";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import { isTauri } from "@/lib/is-tauri";
import { isDenkerInputTarget } from "@/lib/input-bar-target-kind";
import { useAgentStore } from "@/stores/agent-store";
import { inputDestinationKey } from "@/lib/input-destination-key";
import {
  useInputBarTarget,
  type InputBarTarget,
} from "@/stores/input-bar-target-store";
import { useProjectStore } from "@/stores/project-store";
import { useOnboardingStore } from "@/stores/onboarding-store";

type PrewarmReason =
  | "onboarding_runtime_connected"
  | "input_focus"
  | "input_click"
  | "new_chat"
  | "cursor_summon"
  | "dictation_start"
  | "draft_new_topic"
  | "model_changed"
  | "effort_changed"
  | "conversation_open";

const lastSentAt = new Map<string, number>();
const DEDUPE_MS = 12_000;
// Keyed by destination, not project: the dedupe never spans accounts.
subscribeDesktopChatAuthScope(() => lastSentAt.clear());

/** The Denker runtime's identity (template, adapter, model, effort), so a
 *  runtime switch warms again. Denker's template is the same in every
 *  project; the loaded roster only tells its settings. */
function denkerRuntimeSignature(): string {
  const agentStore = useAgentStore.getState();
  const agent = resolveDenkerProjectAgent(agentStore.agents);
  const template = agentStore.templates.find(
    (item) => item.id === agent?.agent_template,
  );
  const config = template?.adapter_config ?? {};
  return [
    template?.adapter_type ?? "",
    config.runtime_source ?? "",
    config.model ?? "",
    config.thinking_effort ?? "",
  ].join(":");
}

/** Where the input bar's next prompt goes: a new Denker chat, the targeted
 *  conversation in any project, or (no target, or another agent's chat)
 *  Denker's current conversation. The viewed project travels only as a hint. */
export function composingRoute(startNewOverride?: boolean): {
  viewedProjectId: string | undefined;
  startNewConversation: boolean;
  /** A new chat for a conversation frame runs in that frame's project. */
  conversationFrameId: string | null;
  target: InputBarTarget | null;
} {
  const routing = useInputBarTarget.getState();
  const startNewConversation = startNewOverride ?? routing.startNewConversation;
  return {
    viewedProjectId: useProjectStore.getState().currentProject?.id,
    startNewConversation,
    conversationFrameId: startNewConversation
      ? routing.newConversationFrameId
      : null,
    target:
      !startNewConversation && isDenkerInputTarget(routing.target)
        ? routing.target
        : null,
  };
}

export function prewarmAgentSession(
  reason: PrewarmReason,
  options: {
    agentId?: string;
    force?: boolean;
    startNewConversation?: boolean;
    /** The chat a likely topic split leaves: the backend warms the new
     *  conversation on that chat's runtime, as the split itself runs. */
    splitFromConversationId?: string;
  } = {},
): void {
  const desktop = isTauri();
  if (desktop) {
    const onboarding = useOnboardingStore.getState();
    // Setup shortcuts and provider activation do not start a workspace task.
    // Explicit first-prompt readiness has its own onboarding-safe API.
    if (
      !onboarding.stepHydrated ||
      onboarding.restarting ||
      onboarding.pendingCompletion ||
      (onboarding.step !== "completed" && onboarding.step !== "skipped")
    )
      return;
  }
  const ws = getCurrentDenkerWs();
  if (!ws) return;
  const { viewedProjectId, startNewConversation, conversationFrameId, target } =
    composingRoute(options.startNewConversation);

  // A durable session id does not prove its local CLI process is still alive.
  // Let the backend and companion check liveness for the exact selected chat.
  const splitFrom = startNewConversation
    ? options.splitFromConversationId
    : undefined;
  const destination = inputDestinationKey({
    startNewConversation,
    conversationId: target?.conversationId,
  });
  const dedupeKey = `${denkerRuntimeSignature()}:${destination}:${splitFrom ?? ""}:${conversationFrameId ?? ""}`;
  const now = Date.now();
  if (!options.force && now - (lastSentAt.get(dedupeKey) ?? 0) < DEDUPE_MS) {
    return;
  }
  lastSentAt.set(dedupeKey, now);

  ws.sendRaw("runtime.prewarm", {
    ...(viewedProjectId ? { project_id: viewedProjectId } : {}),
    reason,
    surface: desktop ? "desktop" : "web",
    ...(startNewConversation ? { start_new_conversation: true } : {}),
    ...(splitFrom ? { split_from_conversation_id: splitFrom } : {}),
    ...(conversationFrameId
      ? { conversation_frame_id: conversationFrameId }
      : {}),
    ...(target
      ? {
          conversation_id: target.conversationId,
          project_agent_id: target.projectAgentId,
        }
      : {}),
  });
}
